import Phaser from "phaser";
import { GAME_HEIGHT, GAME_WIDTH, STORAGE_KEYS } from "../constants";
import { addAudioToggles, addCoverImage, addPanel, dimScene, fitLogo } from "../ui/chrome";
import { MenuButton, bindMenuKeys } from "../ui/MenuButton";
import { bodyStyle, prefersReducedMotion, titleStyle } from "../ui/theme";
import { addWoodText } from "../ui/woodText";
import { audioManager } from "../systems/AudioManager";
import { assetOrigin } from "../assets/loader";

export class MenuScene extends Phaser.Scene {
  private unbindMain: (() => void) | null = null;
  private unbindConfirm: (() => void) | null = null;
  private menuButtons: MenuButton[] = [];
  private quitOpen = false;
  private wakeGate: Phaser.GameObjects.Container | null = null;

  constructor() {
    super("Menu");
  }

  create(): void {
    this.quitOpen = false;
    void audioManager.unlock();
    audioManager.playMusic("menu");
    addCoverImage(this, "menu-background");

    const logo = fitLogo(this, GAME_WIDTH / 2, 168, 760, 280);
    this.addMenuCredits(logo);

    if (this.textures.exists("bear-idle-menu")) {
      this.addMenuBear();
    }

    const start = new MenuButton(this, 268, 360, "Start Run", () => this.startRun());
    const how = new MenuButton(this, 268, 436, "How to Play", () => this.scene.start("HowToPlay"));
    const scores = new MenuButton(this, 268, 512, "High Scores", () => this.scene.start("HighScores"));
    const quit = new MenuButton(this, 268, 588, "Sweet dreams", () => this.confirmQuit(), 236, "secondary");
    this.menuButtons = [start, how, scores, quit];
    this.unbindMain = bindMenuKeys(this, this.menuButtons);

    this.add
      .text(
        GAME_WIDTH / 2,
        GAME_HEIGHT - 24,
        "Arrows or WASD to move  ·  Up / W / Space to jump  ·  Enter to choose  ·  Esc to pause",
        bodyStyle(14),
      )
      .setOrigin(0.5)
      .setColor("#f3e6c8")
      .setStroke("#2a1a10", 4)
      .setWordWrapWidth(1100);

    addAudioToggles(this, 56, 48, 46, 200);
    this.showWakeGateIfNeeded();
  }

  private showWakeGateIfNeeded(): void {
    if (audioManager.unlocked) {
      return;
    }
    this.unbindMain?.();
    this.unbindMain = null;
    const blocker = dimScene(this, 0.5).setDepth(80);
    const label = this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT / 2, "Click or press a key to wake the forest", titleStyle(28))
      .setOrigin(0.5)
      .setDepth(81);
    this.wakeGate = this.add.container(0, 0, [blocker, label]).setDepth(80);
    const dismiss = (): void => {
      if (!this.wakeGate) {
        return;
      }
      void audioManager.unlock().then(() => {
        if (audioManager.musicEnabled) {
          audioManager.playMusic("menu");
        }
      });
      this.wakeGate.destroy();
      this.wakeGate = null;
      this.unbindMain = bindMenuKeys(this, this.menuButtons);
    };
    this.input.once("pointerdown", dismiss);
    this.input.keyboard?.once("keydown", dismiss);
  }

  private startRun(): void {
    const seen = localStorage.getItem(STORAGE_KEYS.howto) === "1";
    if (!seen) {
      this.scene.start("HowToPlay", { next: "Game" });
      return;
    }
    this.scene.start("Game");
  }

  private confirmQuit(): void {
    if (this.quitOpen) {
      return;
    }
    this.quitOpen = true;
    this.menuButtons.forEach((button) => button.setEnabled(false));
    this.unbindMain?.();
    this.unbindMain = null;

    const blocker = dimScene(this).setDepth(50);
    const panel = addPanel(this, GAME_WIDTH / 2, 360, 420, 240);
    panel.setDepth(51);
    const title = this.add
      .text(GAME_WIDTH / 2, 292, "Leave the forest?", titleStyle(28))
      .setOrigin(0.5)
      .setColor("#2a1a10")
      .setStroke("#000000", 0)
      .setDepth(52);
    const yes = new MenuButton(this, GAME_WIDTH / 2 - 110, 390, "Yes", () => this.scene.start("Farewell"), 180);
    const no = new MenuButton(this, GAME_WIDTH / 2 + 110, 390, "Stay", () => dismiss(), 180, "secondary");
    yes.container.setDepth(52);
    no.container.setDepth(52);
    this.unbindConfirm = bindMenuKeys(this, [no, yes]);

    const dismiss = (): void => {
      this.unbindConfirm?.();
      this.unbindConfirm = null;
      blocker.destroy();
      panel.destroy();
      title.destroy();
      yes.container.destroy();
      no.container.destroy();
      this.menuButtons.forEach((button) => button.setEnabled(true));
      this.unbindMain = bindMenuKeys(this, this.menuButtons);
      this.quitOpen = false;
    };
  }

  private addMenuCredits(logo: Phaser.GameObjects.Image): void {
    const y = logo.y + logo.displayHeight * (1 - logo.originY) + 4;
    addWoodText(this, GAME_WIDTH / 2, y, "DESIGNED BY KYLA", 15);
    addWoodText(this, GAME_WIDTH / 2, y + 20, "DEVELOPED BY SMOKETOWN SOFTWARE", 15);
  }

  private addMenuBear(): void {
    for (const key of ["bear-idle-menu-home", "bear-idle-menu-rest", "bear-idle-menu-leaf"]) {
      if (this.anims.exists(key)) {
        this.anims.remove(key);
      }
    }

    const restScale = 0.72;
    const bearX = 1000;
    const bearY = 470;
    const bear = this.add.sprite(bearX, bearY, "bear-idle-menu", 0);
    const origin = assetOrigin("bear-idle-menu");
    bear.setOrigin(origin.x, origin.y).setScale(restScale);

    if (!prefersReducedMotion()) {
      this.tweens.add({
        targets: bear,
        scaleX: restScale * 1.025,
        scaleY: restScale * 1.045,
        duration: 2200,
        yoyo: true,
        repeat: -1,
        ease: "Sine.easeInOut",
      });
    }

    if (!this.textures.exists("menu-maple-leaf")) {
      return;
    }

    const noseX = bearX + 46;
    const noseY = bearY - 108;
    const leaf = this.add.image(noseX, noseY, "menu-maple-leaf");
    leaf.setScale(0.3).setAlpha(0).setDepth(bear.depth + 1);

    const playLeafSoon = (): void => {
      this.time.delayedCall(Phaser.Math.Between(5000, 9000), () => {
        if (!leaf.active) {
          return;
        }
        this.tweens.killTweensOf(leaf);
        leaf.setPosition(noseX + 70, noseY - 110).setAngle(-18).setAlpha(0).setScale(0.22);
        this.tweens.add({
          targets: leaf,
          x: noseX + 8,
          y: noseY - 6,
          angle: 12,
          alpha: 1,
          scale: 0.32,
          duration: 1600,
          ease: "Sine.easeOut",
          onComplete: () => {
            this.tweens.add({
              targets: leaf,
              x: noseX + 6,
              y: noseY - 10,
              angle: -8,
              duration: 700,
              yoyo: true,
              ease: "Sine.easeInOut",
              onComplete: () => {
                this.tweens.add({
                  targets: leaf,
                  x: noseX + 90,
                  y: noseY - 130,
                  angle: 50,
                  alpha: 0,
                  scale: 0.18,
                  duration: 1400,
                  ease: "Sine.easeIn",
                  onComplete: playLeafSoon,
                });
              },
            });
          },
        });
      });
    };

    playLeafSoon();
  }
}
